// SpotrebaView — KTO (odberateľ/partia) koľko ČOHO (PLU) vyfasoval z VSOST/VSZAK
// Mirko 2026-06-29: štatistika spotreby po partiách / položkách (rotory, statory, …)
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader, KpiRow, KpiCard, SearchInput, FilterChips, EmptyPane } from '../components/Ui.jsx'
import { useBreadcrumb } from '../components/Layout.jsx'
import { useToast } from '../components/Toast.jsx'
import { COLORS, TYPO, SPACE, RADIUS, SK } from '../theme.js'
import { useAuth } from '../auth/AuthContext.jsx'
import { fetchAuth } from '../auth/fetchAuth.js'

const ROLY = ['admin', 'owner', 'owner_view', 'office_manager', 'accountant']
function noDiac(s) { return String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase() }
const num = (n) => (Math.round((Number(n) || 0) * 100) / 100).toLocaleString('sk-SK')

export default function SpotrebaView() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  const { setTrail } = useBreadcrumb()
  const [rows, setRows] = useState([])
  const [span, setSpan] = useState(null)
  const [loading, setLoading] = useState(true)
  const [typ, setTyp] = useState('VSOST')           // VSOST | VSZAK | all
  const [mode, setMode] = useState('partia')        // partia | plu
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState({})              // rozbalené skupiny

  useEffect(() => {
    setTrail([{ label: 'Matrix v2', onClick: () => navigate('/') }, { label: 'Spotreba — kto čo fasoval' }])
    return () => setTrail([])
  }, [setTrail, navigate])

  function nacitaj() {
    setLoading(true)
    const qs = new URLSearchParams({ typ })
    if (from) qs.set('from', from); if (to) qs.set('to', to)
    fetchAuth('/api/stats/spotreba?' + qs.toString())
      .then(r => r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)))
      .then(d => { setRows(Array.isArray(d.rows) ? d.rows : []); setSpan(d.span || null) })
      .catch(e => toast.error('Chyba: ' + e.message))
      .finally(() => setLoading(false))
  }
  useEffect(() => { nacitaj() }, [typ, from, to])

  if (!ROLY.includes(user && user.role)) {
    return <div style={{ padding: SPACE.xl }}><PageHeader title="Spotreba" /><EmptyPane icon="" text="Prístup má len vedenie / office." /></div>
  }

  // filter podľa hľadania (partia alebo názov/PLU)
  const fRows = useMemo(() => {
    const q = noDiac(search)
    if (!q) return rows
    return rows.filter(r => noDiac(r.odberatel).includes(q) || noDiac(r.name).includes(q) || noDiac(r.plu).includes(q))
  }, [rows, search])

  // zoskupenie
  const groups = useMemo(() => {
    const m = new Map()
    for (const r of fRows) {
      const key = mode === 'partia' ? (r.kod + '|' + r.odberatel) : (r.plu + '|' + r.name)
      if (!m.has(key)) m.set(key, {
        key,
        head: mode === 'partia' ? r.odberatel : (r.plu + ' · ' + r.name),
        sub: mode === 'partia' ? (r.kod ? 'kód ' + r.kod : '') : (r.unit || ''),
        amount: 0, qty: 0, items: [],
      })
      const g = m.get(key)
      g.amount += Number(r.amount) || 0
      g.qty += Number(r.qty) || 0
      g.items.push(r)
    }
    return [...m.values()].sort((a, b) => b.amount - a.amount)
  }, [fRows, mode])

  const totalAmount = useMemo(() => groups.reduce((s, g) => s + g.amount, 0), [groups])
  const partiaCount = useMemo(() => new Set(fRows.map(r => r.kod + '|' + r.odberatel)).size, [fRows])
  const pluCount = useMemo(() => new Set(fRows.map(r => r.plu)).size, [fRows])
  // priemer/mesiac — počet mesiacov v rozsahu (filter alebo span dát)
  const months = useMemo(() => {
    const a = from || (span && span.mn), b = to || (span && span.mx)
    if (!a || !b) return 1
    const dd = (new Date(b).getTime() - new Date(a).getTime()) / (1000 * 60 * 60 * 24 * 30.4)
    return Math.max(1, dd)
  }, [from, to, span])

  function exportCsv() {
    const head = ['Odberateľ', 'Kód', 'PLU', 'Názov', 'MJ', 'Množstvo', 'Dokladov', 'Suma_EUR']
    const esc = (v) => String(v == null ? '' : v).replace(/[;\r\n]/g, ' ')
    const lines = [head.join(';')]
    fRows.forEach(r => lines.push([esc(r.odberatel), esc(r.kod), esc(r.plu), esc(r.name), esc(r.unit), r.qty, r.docs, Math.round((r.amount || 0) * 100) / 100].join(';')))
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `spotreba_${typ}_${(span && span.mx) || ''}.csv`; a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div style={{ padding: SPACE.lg, height: '100%', overflowY: 'auto', boxSizing: 'border-box' }}>
      <PageHeader title="Spotreba — kto čo fasoval" subtitle={span && span.mn ? `dáta ${span.mn} … ${span.mx}` : null} />

      <div style={{ display: 'flex', gap: SPACE.md, alignItems: 'center', flexWrap: 'wrap', margin: `${SPACE.sm}px 0 ${SPACE.md}px` }}>
        <FilterChips options={[['VSOST', 'VSOST (pomôcky/partia)'], ['VSZAK', 'VSZAK (na zákazku)'], ['all', 'Oba']].map(([k, l]) => ({ key: k, label: l }))} value={typ} onChange={setTyp} equalWidth={false} />
        <FilterChips options={[['partia', 'Po partiách'], ['plu', 'Po položkách (PLU)']].map(([k, l]) => ({ key: k, label: l }))} value={mode} onChange={setMode} equalWidth={false} />
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: TYPO.sizes.sm, color: COLORS.muted }}>
          od <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={dateInp} />
          do <input type="date" value={to} onChange={e => setTo(e.target.value)} style={dateInp} />
          {(from || to) && <button onClick={() => { setFrom(''); setTo('') }} style={{ ...dateInp, cursor: 'pointer', border: 'none', background: COLORS.bgSoft }}>×</button>}
        </span>
        <button onClick={exportCsv} disabled={!fRows.length} title="Export do CSV (pre účtovníčku)" style={{ marginLeft: 'auto', fontSize: TYPO.sizes.sm, padding: '4px 12px', border: `1px solid ${COLORS.add}`, background: '#fff', color: COLORS.add, borderRadius: RADIUS.sm, cursor: fRows.length ? 'pointer' : 'not-allowed', fontFamily: 'inherit', opacity: fRows.length ? 1 : 0.5 }}>Export CSV</button>
      </div>

      <KpiRow cols={4}>
        <KpiCard title={mode === 'partia' ? 'Partie / odberatelia' : 'Položky (PLU)'} value={mode === 'partia' ? partiaCount : pluCount} color={COLORS.nav} />
        <KpiCard title="Druhov PLU" value={pluCount} color={COLORS.info} />
        <KpiCard title="Σ spotreba (nákl. cena)" value={num(totalAmount) + ' €'} color={COLORS.brand} />
        <KpiCard title={`Priemer / mesiac (~${months.toFixed(1)} mes.)`} value={num(totalAmount / months) + ' €'} color={COLORS.add} />
      </KpiRow>

      <div style={{ margin: `${SPACE.md}px 0` }}>
        <SearchInput value={search} onChange={setSearch} placeholder="Hľadať partiu, PLU alebo názov…" />
      </div>

      {loading ? <div style={{ color: COLORS.muted, padding: SPACE.md }}>Načítavam…</div>
        : groups.length === 0 ? <EmptyPane icon="" text="Žiadne výdajky pre zvolený filter." />
        : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {groups.map(g => {
              const isOpen = !!open[g.key]
              return (
                <div key={g.key} style={{ border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, overflow: 'hidden', background: COLORS.bg }}>
                  <div onClick={() => setOpen(p => ({ ...p, [g.key]: !p[g.key] }))}
                    style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm, padding: '8px 12px', cursor: 'pointer', background: isOpen ? COLORS.navBg : 'transparent' }}>
                    <span style={{ color: COLORS.nav, fontWeight: TYPO.weights.bold, width: 14 }}>{isOpen ? '▾' : '▸'}</span>
                    <span style={{ flex: 1, fontWeight: TYPO.weights.semibold }}>{g.head}{g.sub ? <span style={{ color: COLORS.muted, fontWeight: TYPO.weights.normal, fontSize: TYPO.sizes.xs }}> · {g.sub}</span> : null}</span>
                    <span style={{ color: COLORS.muted, fontSize: TYPO.sizes.xs }}>{mode === 'partia' ? g.items.length + ' PLU' : ''}</span>
                    {mode === 'plu' && <span style={{ fontFamily: TYPO.mono, fontSize: TYPO.sizes.sm }}>{num(g.qty)} {g.items[0] && g.items[0].unit}</span>}
                    <span style={{ fontFamily: TYPO.mono, fontWeight: TYPO.weights.bold, minWidth: 110, textAlign: 'right' }}>{num(g.amount)} €</span>
                  </div>
                  {isOpen && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: TYPO.sizes.xs, background: COLORS.bgSoft }}>
                      <thead>
                        <tr style={{ borderTop: `1px solid ${COLORS.line}`, color: COLORS.muted }}>
                          <th style={th}>{mode === 'partia' ? 'PLU' : 'Odberateľ (partia)'}</th>
                          <th style={th}>{mode === 'partia' ? 'Názov' : 'Kód'}</th>
                          <th style={{ ...th, textAlign: 'right' }}>Množstvo</th>
                          <th style={{ ...th, textAlign: 'right' }}>Dokladov</th>
                          <th style={{ ...th, textAlign: 'right' }}>Suma €</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.items.sort((a, b) => b.amount - a.amount).map((r, i) => (
                          <tr key={i} style={{ borderTop: `1px solid ${COLORS.lineSoft}` }}>
                            <td style={td}>{mode === 'partia' ? r.plu : r.odberatel}</td>
                            <td style={td}>{mode === 'partia' ? r.name : (r.kod || '—')}</td>
                            <td style={{ ...td, textAlign: 'right', fontFamily: TYPO.mono }}>{num(r.qty)} {r.unit}</td>
                            <td style={{ ...td, textAlign: 'right', fontFamily: TYPO.mono, color: COLORS.muted }}>{r.docs}</td>
                            <td style={{ ...td, textAlign: 'right', fontFamily: TYPO.mono, fontWeight: TYPO.weights.semibold }}>{num(r.amount)} €</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )
            })}
          </div>
        )}
    </div>
  )
}

const dateInp = { fontSize: TYPO.sizes.sm, padding: '3px 6px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontFamily: 'inherit', color: COLORS.text, background: COLORS.bg }
const th = { padding: '5px 10px', textAlign: 'left', fontWeight: TYPO.weights.semibold }
const td = { padding: '4px 10px' }
